import { z } from "zod";
import type {
  StepConfig,
  StepCategory,
  StepLibraryModule,
  ConfigTypeId,
  StepTypeId,
} from "./index";
import type { SystemEvent } from "@erp/shared-types";
import type { Permission } from "@erp/authz";
import type { VisibilityRule } from "./visibility";

/**
 * Helper function to create a workflow step/tool config with full type inference.
 *
 * defineStepConfig is an alias — keep a single implementation to avoid
 * step vs tool divergence. Any "step-only" fields (like `deprecated`) are
 * optional and harmless on tool configs.
 */
export function defineToolConfig<
  TConfig extends z.ZodType,
  TInput extends z.ZodType,
  TOutput extends z.ZodType,
  TEmits extends readonly SystemEvent[] = readonly [],
>(params: {
  type: ConfigTypeId;
  name: string;
  description: string;
  category: StepCategory;
  libraryModule?: StepLibraryModule;
  inputSchema: TInput;
  outputSchema: TOutput;
  configSchema: TConfig;
  emits: TEmits;
  requiredPermissions?: Permission[];
  execution?: {
    startToCloseTimeout?: string;
    scheduleToCloseTimeout?: string;
    heartbeatTimeout?: string;
    retry?: {
      maximumAttempts?: number;
      backoffCoefficient?: number;
      initialInterval?: string;
      maximumInterval?: string;
    };
    nonRetryableErrorTypes?: string[];
  };
  /**
   * Configure this step to wait for an external signal before executing.
   *
   * ⚠️ CAUTION: This is a powerful feature that should be used sparingly.
   * Steps with waitForSignal will pause the workflow until a signal arrives,
   * then execute repeatedly until `resolved: true` is returned.
   *
   * Requirements:
   * - Step handler MUST return `{ resolved: boolean, ... }` in its output
   * - Step should be idempotent (may execute multiple times per signal)
   * - Step should be lightweight (executes on every signal until resolved)
   *
   * @see backend/src/workflows/temporal/workflows/helpers/waitForSignal.ts
   */
  waitForSignal?: {
    /** Name of the signal to wait for */
    signalName: string;
    /** Timeout in milliseconds (default: 7 days) */
    timeoutMs?: number;
    /** Maximum iterations before failing (default: 50) */
    maxIterations?: number;
  };
  toolTypes?: string[];
  visibility?: VisibilityRule;
  deprecated?: {
    reason: string;
    replacedBy?: StepTypeId;
  };
  evalConfig?: {
    evalOutputKeys: string[];
    outputFormat?: "parallel_arrays" | "object";
    contextOutputKeys?: string[];
  };
}): StepConfig<TConfig, TInput, TOutput, TEmits> {
  return params;
}

// DO NOT split defineStepConfig and defineToolConfig into separate functions.
// Steps and AI tools are converging on the same registration/config mechanism.
// If a tool type isn't in StepTypeId yet, add it there — don't fork the helper.
export const defineStepConfig = defineToolConfig;
